import { Component, EventEmitter, Input, NgZone, OnInit, Output, Signal, WritableSignal, computed, inject, signal, Inject, PLATFORM_ID } from '@angular/core';
import { DeliveryAddress } from '../signup.types';
import { AddressService, NavegoAddressRequest } from '../../../shared/address.service';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatStep } from '@angular/material/stepper';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { ArrayOptionsItem, DeliveryDay, DeliveryOptionsResponseItem, PickUpOption } from '../../../shared/types/account.types';
import { environment } from '../../../../environments/environment';
import { Session } from '../../../shared/types/session.type';
import { AccountService } from '../../../shared/account.service';
import { AutocompleteInputComponent } from '../autocomplete-input/autocomplete-input.component';
import { LocalStorageService } from '../../../shared/local-storage.service';
import { LOCALSTORAGE_KEYS } from '../../../shared/constants/databases';
import { finalize, tap } from 'rxjs';
import { formatDateToReadableString } from '../../../shared/utils/formatting';
import { NotificationService } from '../../../shared/notification/notification.service';
import { DataLayerService } from '../../../shared/data-layer/data-layer.service';
import { DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS, DataLayer_SignUpFunnel_Pickup } from '../../../shared/data-layer/data-layer.types';
import { isEmailValid } from '../../../shared/common/email-validator.util';
import { ModalContentService } from '../../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../../shared/constants/modal-content-types';
import { Router } from '@angular/router';
import { AuthService } from '../../auth.service';

@Component({
  selector: 'app-check-address-signup',
  providers: [],
  imports: [
    CommonModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatRadioModule,
    AutocompleteInputComponent,
  ],
  templateUrl: './check-address-signup.component.html',
  styleUrl: './check-address-signup.component.scss'
})
export class CheckAddressSignupComponent implements OnInit {
  private localStorageService = inject(LocalStorageService)
  private addressService = inject(AddressService);
  private signalsStoreService = inject(SignalsStoreService);
  private accountService = inject(AccountService);
  #notificationService = inject(NotificationService);
  #dataLayerService = inject(DataLayerService);
  #modalContentService = inject(ModalContentService);
  #router = inject(Router);
  #authService = inject(AuthService);

  @Input() firstStep!: MatStep;
  @Output() onNextStep: EventEmitter<any> = new EventEmitter<any>();
  @Output() deliveryAddressChanged = new EventEmitter<WritableSignal<Partial<DeliveryAddress>>>();
  @Output() selectedDayChanged = new EventEmitter<any>();
  @Output() selectedTimeWindowChanged = new EventEmitter<any>();
  @Output() deliveryOptionChanged = new EventEmitter<any>();
  @Output() emailChanged = new EventEmitter<string>();
  @Output() pickupInfoChanged = new EventEmitter<DataLayer_SignUpFunnel_Pickup>();
  isAnonymousExplorer = computed(() => this.signalsStoreService.isAnonymousExplorer());

  /** The day the explorer's carts are dated for, straight off the session the
   *  backend hands back (`deliveryInfo.deliveryDayId`, present on both
   *  `/explorer` and `/explorer/activate`). Changing it makes the backend
   *  cancel them, so it is preselected on both fulfilment branches and the
   *  change is called out rather than discovered (DEV-11262). */
  #explorerDeliveryDayId(): number | null {
    if (!this.isAnonymousExplorer()) return null;

    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    return session?.deliveryInfo?.deliveryDayId ?? null;
  }

  /** The zone the explorer's carts are priced and dated for. */
  #explorerZoneId = computed(() => {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    return session?.address?.zoneId ?? null;
  });

  /** True once the explorer has moved off the zone or the pickup day their
   *  carts belong to — which is what makes the backend cancel them. */
  explorerSelectionChanged = computed(() => {
    if (!this.isAnonymousExplorer()) return false;

    const zoneId = this.#explorerZoneId();
    const currentZone = this.addressService.navegoAddress()?.zoneId ?? null;
    if (zoneId && currentZone && Number(zoneId) !== Number(currentZone)) return true;

    const explorerDayId = this.#explorerDeliveryDayId();
    const pickUpDay = this.selectedPickUpDeliveryDay();
    return !!explorerDayId && !!pickUpDay
      && Number(pickUpDay.deliveryDay ?? pickUpDay.id) !== Number(explorerDayId);
  });

  addressWithoutZipCode = computed(() => this.signalsStoreService.addressWithoutZipCode());

  deliveryAddress: WritableSignal<Partial<DeliveryAddress>> = signal({
    additionalAddress: '',
    address: '',
    city: '',
    deliveryNotes: '',
    stateCode: '',
    zipCode: '',
  })

  deliveryOption: any[] = [];
  deliveryOptionSelected: WritableSignal<any> = signal(null);

  validSelectedAddress = signal<any>(null);

  //#region Email capture (elevated from the personal-info step, mirrors pp/pb)
  signupEmail = signal('');
  isSignupEmailValid = computed(() => isEmailValid(this.signupEmail()));
  isEmailAvailable = signal<boolean>(false);
  isCheckingEmail = signal<boolean>(false);
  emailAlreadyInUse = signal<boolean>(false);
  #lastCheckedEmail = signal<string>('');
  //#endregion

  //#region Pickup Flow
  pickUpOptions: PickUpOption[] = [];
  selectedPickUpOption: WritableSignal<PickUpOption | null> = signal(null);
  pickUpDeliveryDays: DeliveryDay[] = [];
  selectedPickUpDeliveryDay: WritableSignal<any> = signal(null);
  zoneId: number = 0;
  //#endregion

  //#region Check Address Flow
  selectedDeliveryDay: WritableSignal<DeliveryOptionsResponseItem | null> = signal(null);
  checkAddressDeliveryDays!: DeliveryOptionsResponseItem;
  timeSet: Signal<ArrayOptionsItem[]> = computed(() => this.setUpTimeSet());
  selectedDeliveryTimeWindow: any = signal(null);
  //#endregion

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.updateDeliverySelection();
    this.getAllDeliveryOptions();
    this.addressService.setFirstPickUpMembershipSelected$.pipe(tap((value) => value ? this.selectFirstPickUpMembership() : null)).subscribe();
    this.#loadPreloadedEmail();
  }

  #loadPreloadedEmail(): void {
    const preloaded = this.localStorageService.get(LOCALSTORAGE_KEYS.PRELOADED_EMAIL) as string | null;
    if (!preloaded || this.signupEmail() === preloaded) return;
    this.signupEmail.set(preloaded);
    this.#lastCheckedEmail.set('');
    this.isEmailAvailable.set(false);
    this.localStorageService.remove(LOCALSTORAGE_KEYS.PRELOADED_EMAIL);
    this.onEmailBlur();
  }

  clearEmailField() {
    this.signupEmail.set('');
    this.isEmailAvailable.set(false);
    this.emailAlreadyInUse.set(false);
    this.#lastCheckedEmail.set('');
  }

  onEmailChange(value: string) {
    this.signupEmail.set(value);
    if (value !== this.#lastCheckedEmail()) {
      this.isEmailAvailable.set(false);
      this.emailAlreadyInUse.set(false);
    }
  }

  onEmailBlur() {
    // An explorer's email is already theirs and is locked on screen — asking
    // the server whether it is taken would only answer "yes, by you".
    if (this.isAnonymousExplorer()) return;

    const email = this.signupEmail().trim();
    if (!email || !this.isSignupEmailValid()) return;
    if (email === this.#lastCheckedEmail() && this.isEmailAvailable()) return;

    this.isCheckingEmail.set(true);
    this.accountService.checkEmail(email)
      .pipe(finalize(() => this.isCheckingEmail.set(false)))
      .subscribe({
        next: (data) => {
          this.#lastCheckedEmail.set(email);

          if (!data?.emailUsed) {
            this.isEmailAvailable.set(true);
            this.emailAlreadyInUse.set(false);
            return;
          }

          this.isEmailAvailable.set(false);

          // "Already in use" is the wrong story for an explorer: the account is
          // theirs and unfinished. Pushing them at a login they have no
          // password for is the dead end this flow exists to remove, so hand
          // them back to the check-address modal, which asks for the address
          // and day again and resumes them (DEV-11262).
          //
          // Decided before the field is marked, not after: setting it first
          // flashes the wrong message on the way to the right one.
          if (environment.config.flows.explorer.enabled && data.isAnonymousExplorer) {
            this.#authService.resumeExplorer(email);
            return;
          }

          this.emailAlreadyInUse.set(true);
          this.#showModalEmailAlreadyInUse(email);
        },
        error: () => {
          this.isEmailAvailable.set(true);
          this.emailAlreadyInUse.set(false);
        }
      });
  }

  #showModalEmailAlreadyInUse(email: string) {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Email already in use',
      textContent: `Hey! Looks like you already have an account with us. Would you like to login with <b><i>${email}</i></b>?`,
      cancelButtonText: 'Try another email',
      confirmButtonText: 'Click here to login',
    }).closed.subscribe((res: { confirm: boolean } | null) => {
      if (!res?.confirm) {
        this.clearEmailField();
        return;
      }
      this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
      this.#router.navigate(['/login']);
    });
  }

  private getAllDeliveryOptions() {
    this.accountService.getAllCustomerTypes().subscribe(deliveryOption => {
      this.deliveryOption = deliveryOption.data;
      const storedDeliveryOptionSelected: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SELECTED_DELIVERY_PREFERENCE);
      const signupSelectedMembership: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP);
      const selectedDeliveryOption = this.deliveryOption.find(d => d.id === storedDeliveryOptionSelected?.id || d.id === signupSelectedMembership?.id) || this.deliveryOption[0];
      this.deliveryOptionSelected.set(selectedDeliveryOption);
      if (!this.deliveryOptionSelected()?.configuration?.requireAddress)
        this.getPickupOptions(this.deliveryOptionSelected())
    });
  }

  getPickupOptions(membership: any) {
    if (!membership?.id) return;
    this.accountService.getPickUpOption(membership.id).subscribe(pickUpOptions => {
      this.pickUpOptions = pickUpOptions.map((l: { composedName: string; name: any; locationAddress: any; }) => {
        l.composedName = `${(l.name ? `${l.name} - ` : '') || ''}${l.locationAddress}`;
        return l;
      });
      const storedPickUpLocation: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SELECTED_PICKUP_OPTION);
      if (storedPickUpLocation)
        this.setSelectedPickUpOption({ value: storedPickUpLocation.id })

      if (this.pickUpOptions.length === 1)
        this.setSelectedPickUpOption({ value: this.pickUpOptions[0].id });
    });
  }

  #explorerDay(options: DeliveryOptionsResponseItem[]): DeliveryOptionsResponseItem | null {
    const dayId = this.#explorerDeliveryDayId();
    if (!dayId) return null;

    return options.find(o => Number(o.id) === Number(dayId)) ?? null;
  }

  /** The pickup branch's half of the same problem. Its days belong to the
   *  location rather than a zone, so an explorer's day has to be found again
   *  every time the location changes — and when it is not among them, leaving
   *  the selector empty is right: whatever they pick there really will cancel
   *  the carts, and the warning says so. */
  #preselectExplorerPickUpDay() {
    const dayId = this.#explorerDeliveryDayId();
    if (!dayId) return;

    const day = this.pickUpDeliveryDays.find(d => Number(d.id) === Number(dayId));
    if (day) this.selectedPickUpDeliveryDay.set({ ...day, deliveryDay: day.id, zoneId: this.zoneId });
  }

  private getDeliveryOptions() {
    this.accountService
      .getDeliveryOptions(this.addressService.navegoAddress()?.zoneId)
      .subscribe({
        next: (options: DeliveryOptionsResponseItem[]) => {
          if (options && options.length) {
            options.forEach(x => x.formattedName = this.#formattDeliveryDay(x.deliveryDate ?? ''))
            // An explorer already has a day, and their carts are dated for it.
            // Landing them on options[0] would silently move it and throw the
            // basket away the moment they activate. The display follows the
            // selection: this flow shows the day, it does not let them pick.
            const explorerDay = this.#explorerDay(options);
            this.checkAddressDeliveryDays = explorerDay ?? options[0];
            this.selectedDeliveryDay.set(explorerDay ?? options[0]);
          }
        }
      });
  }

  #formattDeliveryDay(date: string): string {
    const { dayName, day, month } = formatDateToReadableString(date);
    return `${dayName}, ${month} ${day}`;
  }

  updateDeliveryAddress(newValue: any) {
    this.deliveryAddress.update((value) => ({ ...value, ...newValue }))
  }

  setSelectedPickUpOption(event: any) {
    this.selectedPickUpDeliveryDay.set(null)
    const option = this.pickUpOptions.find(o => o.id === event.value);
    if (option) {
      this.selectedPickUpOption.set(option);
      this.zoneId = option.id
      this.pickUpDeliveryDays = option.deliveryDays;
      this.#preselectExplorerPickUpDay();
    }
  }

  setSelectedPickUpDeliveryDays(event: MatSelectChange) {
    const option = this.pickUpDeliveryDays.find(o => o.id === event.value);
    if (option) {
      this.selectedPickUpDeliveryDay.set({ ...option, deliveryDay: option.id, zoneId: this.zoneId });
    }
  }

  public updateDeliverySelection(restart: boolean = false, loadPickups: boolean = false) {
    if (restart) {
      this.validSelectedAddress.set(null);
    }
    this.updateDeliveryAddress({ 'deliveryOptionSelected': this.deliveryOptionSelected() });
    this.selectedPickUpDeliveryDay.set(null);
  }

  private setUpTimeSet() {
    const selectedDay = this.selectedDeliveryDay();
    return (selectedDay && selectedDay.timeSet && selectedDay.timeSet.length) ? [{ id: -1, name: "No preferences" }, ...selectedDay.timeSet] : [];
  }

  shouldDisabledPickUpFlow() {
    return !this.selectedPickUpDeliveryDay() || !this.selectedPickUpOption() || !this.isSignupEmailValid() || !this.isEmailAvailable() || this.isCheckingEmail()
  }

  shouldDisableCheckAddresFlow() {
    return !this.selectedDeliveryDay() || !this.deliveryOptionSelected() || !this.validSelectedAddress() || this.deliveryAddress().deliveryNotes == '' || (this.deliveryAddress().zipCode?.length ?? 0) < 5 || !this.isSignupEmailValid() || !this.isEmailAvailable() || this.isCheckingEmail()
  }

  prevContinuePickup() {

    const resolver = (message: string) => {
      this.#notificationService.show({ text: message, type: 'error' });
    }

    if (!this.selectedPickUpOption()?.id)
      return resolver('Please select a valid pickup address.');
    if (!this.selectedPickUpDeliveryDay()?.id)
      return resolver('Please select a valid pickup day.');
    if (!this.selectedPickUpDeliveryDay()?.deliveryWindow)
      return resolver('Please select a valid pickup window.');
    if (!this.isSignupEmailValid())
      return resolver('Please enter a valid email address.');
    if (this.isCheckingEmail())
      return resolver('Please wait while we verify your email.');
    if (!this.isEmailAvailable())
      return resolver('This email is already in use. Please use another or sign in.');

    this.#continue(true);

  }

  prevContinue() {

    const resolver = (message: string) => {
      this.#notificationService.show({ text: message, type: 'error' });
    }

    if (!this.selectedDeliveryDay())
      return resolver('Please select a valid delivery day.');
    if (!this.deliveryOptionSelected())
      return resolver('Please select a valid delivery window.');
    if (!this.validSelectedAddress())
      return resolver('Please provide a valid delivery address.');
    if (this.deliveryAddress().deliveryNotes === '')
      return resolver('Please include delivery instructions or notes.');
    if ((this.deliveryAddress().zipCode?.length ?? 0) < 5)
      return resolver('The ZIP code must contain at least 5 digits.');
    if (!this.isSignupEmailValid())
      return resolver('Please enter a valid email address.');
    if (this.isCheckingEmail())
      return resolver('Please wait while we verify your email.');
    if (!this.isEmailAvailable())
      return resolver('This email is already in use. Please use another or sign in.');

    this.#continue(false);

  }

  #getPickupInfo(): DataLayer_SignUpFunnel_Pickup {
    const option: any = this.selectedPickUpOption();
    const day: any = this.selectedPickUpDeliveryDay();
    return {
      location: option?.composedName ?? option?.locationAddress ?? '',
      day: day?.name ?? '',
      zone: String(day?.zoneId ?? ''),
    };
  }

  #continue(isPickup: boolean = false) {
    if (!isPickup && this.shouldDisableCheckAddresFlow())
      return;

    if (isPickup && this.shouldDisabledPickUpFlow())
      return;

    this.firstStep.completed = true
    this.emailChanged.emit(this.signupEmail());
    this.deliveryAddressChanged.emit(this.deliveryAddress);

    if (this.deliveryOptionSelected()?.configuration?.requireAddress) {
      const nav = this.addressService.navegoAddress();
      if (nav) {
        this.#dataLayerService.trackSignUpFunnelDeliveryAddressEvent({
          funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.delivery_information,
          funnel_step_number: 1,
          delivery_type: 'delivery',
          delivery: {
            address: nav.street,
            city: nav.city,
            state: nav.state,
            zip_code: this.deliveryAddress().zipCode ?? '',
            zone: nav.zoneName,
            in_area: true,
          },
          user: { email: this.signupEmail() },
        });
      }
      this.selectedDayChanged.emit(this.selectedDeliveryDay());
      this.selectedTimeWindowChanged.emit(this.selectedDeliveryTimeWindow())
    } else {
      // PW uses pickup locations: fire the funnel step for pickup too, with its own pickup object.
      const pickup = this.#getPickupInfo();
      this.pickupInfoChanged.emit(pickup);
      this.#dataLayerService.trackSignUpFunnelDeliveryAddressEvent({
        funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.delivery_information,
        funnel_step_number: 1,
        delivery_type: 'pickup',
        pickup,
        user: { email: this.signupEmail() },
      });
      this.selectedDayChanged.emit(this.selectedPickUpDeliveryDay());
      this.selectedTimeWindowChanged.emit(-1); // Pickup locations doesn't requires time window for ME
    }

    this.deliveryOptionChanged.emit(this.deliveryOptionSelected());
    this.localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP, this.deliveryOptionSelected());
    this.onNextStep.emit(this.firstStep);
  }

  selectFirstPickUpMembership() {
    const selected = this.deliveryOption.find(m => !m.configuration?.requireAddress);
    this.deliveryOptionSelected.set(selected);
    this.getPickupOptions(this.deliveryOptionSelected())
  }

  onValidDeliveryAddressSelected(e: any) {
    this.validSelectedAddress.set(e);
    if (!!this.addressService.navegoAddress()) this.getDeliveryOptions();
    const zip = e?.zipCode ?? this.deliveryAddress().zipCode ?? '';
    this.updateDeliveryAddress({ zipCode: e?.zipCode ?? '' })
    // check_address fires on real address checks (in/out of area). Skip the empty
    // emission the autocomplete produces on init, when nothing has been entered yet.
    if (zip || !!e) {
      this.#dataLayerService.trackCheckAddressEvent({
        delivery: {
          zip_code: zip,
          in_area: !!e,
        },
        user: { email: this.signupEmail() },
      });
    }
  }

  checkAddressByZipCode() {
    const { city, street, state, latitude, longitude } = this.signalsStoreService.addressWithoutZipCode() || {};
    const zipCode = this.deliveryAddress().zipCode;
    if (!zipCode || !city || !latitude || !longitude || !state || !street) return;

    const request: NavegoAddressRequest = {
      city,
      latitude,
      longitude,
      state,
      street,
      zipCode
    }

    this.addressService.checkDeliveryAddressByZipCode(request, true, true);
  }

}
